import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuickLog } from '../hooks/useQuickLog'
import styles from './FloatingActionButton.module.css'

type FABAction =
  | { label: string; to: string; onClick?: never; icon: React.ReactNode }
  | { label: string; onClick: () => void; to?: never; icon: React.ReactNode }

interface FloatingActionButtonProps {
  actions?: FABAction[]
}

// Sentinel values — FAB replaces these with the modal open handlers at render time
const LOG_TOUCH_SENTINEL = '__log_touch__'
const MOBILE_QUICK_LOG_SENTINEL = '__mobile_quick_log__'

function LogTouchIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.63 3.36 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.56a16 16 0 0 0 6.08 6.08l1.63-1.63a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  )
}

const defaultActions: FABAction[] = [
  { label: 'Quick Log', to: MOBILE_QUICK_LOG_SENTINEL as string, icon: <LogTouchIcon /> },
  { label: 'New Deal', to: '/deals/new', icon: <DealIcon /> },
  { label: 'New Client', to: '/clients/new', icon: <ClientIcon /> },
  { label: 'Add Prospect', to: '/prospects/new', icon: <ProspectIcon /> },
  { label: 'New Task', to: '/tasks/new', icon: <TaskIcon /> },
]

function DealIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    </svg>
  )
}

function ClientIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  )
}

function ProspectIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  )
}

function TaskIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  )
}

function ComplianceIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  )
}

export default function FloatingActionButton({ actions = defaultActions }: FloatingActionButtonProps) {
  const { open: openQuickLog } = useQuickLog()
  const [isOpen, setIsOpen] = useState(false)

  // Resolve sentinels to real onClick handlers
  const resolvedActions: FABAction[] = actions.map(action => {
    if ('to' in action && action.to === LOG_TOUCH_SENTINEL) {
      // Opens the one Quick Log implementation (AppLayout's, bound to Cmd/Ctrl+L)
      // rather than a second, lesser copy of the same form.
      return { label: action.label, icon: action.icon, onClick: () => { setIsOpen(false); openQuickLog() } }
    }
    if ('to' in action && action.to === MOBILE_QUICK_LOG_SENTINEL) {
      // One quick-log implementation for every surface — the mobile duplicate
      // wrote different fields through a second form and is deleted.
      return { label: action.label, icon: action.icon, onClick: () => { setIsOpen(false); openQuickLog() } }
    }
    return action
  })

  return (
    <>
    <div className={styles.fabContainer}>
      {isOpen && (
        <div className={styles.fabMenu}>
          {resolvedActions.map(action => {
            if ('onClick' in action && action.onClick) {
              return (
                <button
                  key={action.label}
                  className={styles.fabMenuItem}
                  onClick={() => { setIsOpen(false); action.onClick!() }}
                  style={{ background: 'none', cursor: 'pointer', font: 'inherit', textAlign: 'left' }}
                >
                  <div className={styles.fabMenuIcon}>{action.icon}</div>
                  <span>{action.label}</span>
                </button>
              )
            }
            return (
            <Link
              key={action.to}
              to={action.to!}
              className={styles.fabMenuItem}
              onClick={() => setIsOpen(false)}
            >
              <div className={styles.fabMenuIcon}>{action.icon}</div>
              <span>{action.label}</span>
            </Link>
            )
          })}
        </div>
      )}
      <button
        className={`${styles.fab} ${isOpen ? styles.fabOpen : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Quick actions"
      >
        <svg
          viewBox="0 0 24 24"
          width="24"
          height="24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          style={{ transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
        >
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
      </button>
    </div>
    </>
  )
}
